import { Button, Card, Col, Form, Input, message, Modal, Row, Select, Breadcrumb, Divider, Tabs } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ExclamationCircleOutlined, ArrowLeftOutlined, SaveOutlined, DeleteOutlined,
    HomeOutlined, InfoCircleOutlined, PictureOutlined, SettingOutlined,
    SoundOutlined, VideoCameraOutlined, FileTextOutlined, BookOutlined
} from "@ant-design/icons";
import CustomUpload from "../customUpload";
import TextArea from "antd/es/input/TextArea";
import slugify from "slugify";
import convertVttToLrc from "../../utils/convertVttToLrc";

function TrackSection({ label, lrcName, audioName, videoName, required, handleVttChange }) {
    return (
        <div>
            <Form.Item
                label={<span className="font-semibold">{label} — Lời (LRC)</span>}
                name={lrcName}
                rules={required ? [{ required: true, message: "Vui lòng nhập nội dung!" }] : []}
            >
                <TextArea
                    placeholder={`Nhập nội dung ${label} (hỗ trợ paste VTT → tự động chuyển LRC)`}
                    style={{ height: "300px", fontFamily: "monospace", fontSize: "13px" }}
                    onChange={handleVttChange(lrcName)}
                />
            </Form.Item>
            <Row gutter={16}>
                <Col span={12}>
                    <Card size="small" className="bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2 font-semibold flex items-center gap-1">
                            <SoundOutlined className="text-blue-500" /> Audio
                        </p>
                        <Form.Item name={audioName} className="mb-2"
                            rules={required ? [{ required: true, message: "Vui lòng chọn audio!" }] : []}>
                            <Input size="small" placeholder="URL audio" />
                        </Form.Item>
                        <Form.Item name={audioName} className="mb-0">
                            <CustomUpload type="file" accept=".mp3" />
                        </Form.Item>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card size="small" className="bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2 font-semibold flex items-center gap-1">
                            <VideoCameraOutlined className="text-red-500" /> Video
                        </p>
                        <Form.Item name={videoName} className="mb-2">
                            <Input size="small" placeholder="URL video" />
                        </Form.Item>
                        <Form.Item name={videoName} className="mb-0">
                            <CustomUpload type="file" accept=".mp4, .webm, .ogg, .mov, .m4v" />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default function LessonForm({
    id = "",
    initialValues = {},
    onSubmit = () => {},
}) {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [courses, setCourses] = useState([]);
    const [pagination] = useState({ page: 1, pageSize: 100 });

    const fetchCourses = async () => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_URL}/course/getAll`, { params: pagination }
            );
            setCourses(result?.data?.data);
        } catch (e) {
            message.error("Không thể tải danh sách course");
        }
    };

    const deleteLesson = async () => {
        await axios
            .delete(`${process.env.REACT_APP_API_URL}/lesson/delete/${id}`)
            .then(() => {
                message.success("Xoá bài học thành công");
                navigate("/admin/lesson");
            });
    };

    const handleLessonSlugChange = (e) => {
        form.setFieldValue("slug", slugify(e.target.value, { lower: true }));
    };

    useEffect(() => {
        fetchCourses();
        if (Object.keys(initialValues)?.length > 0) {
            form.resetFields();
        }
    }, [form, initialValues]);

    const confirmDelete = () => {
        Modal.confirm({
            icon: <ExclamationCircleOutlined />,
            content: "Bạn có chắc chắn xoá bài học này?",
            okText: "Xác nhận",
            cancelText: "Huỷ",
            onOk: () => deleteLesson(),
        });
    };

    const handleVttChange = (fieldName) => (e) => {
        const value = e.target.value;
        if (value && value.includes('-->')) {
            const converted = convertVttToLrc(value);
            if (converted !== value) {
                form.setFieldValue(fieldName, converted);
                message.success('Đã tự động chuyển đổi VTT sang LRC!');
            }
        }
    };

    const trackTabs = [
        {
            key: "mainStory",
            label: <span className="flex items-center gap-1"><BookOutlined />Main Story</span>,
            children: <TrackSection label="Main Story" lrcName="mainStory" audioName="mainStoryAudio" videoName="mainStoryVideo" required handleVttChange={handleVttChange} />,
        },
        {
            key: "vocabulary",
            label: <span className="flex items-center gap-1"><FileTextOutlined />Vocabulary</span>,
            children: <TrackSection label="Vocabulary" lrcName="vocabulary" audioName="vocabularyAudio" videoName="vocabularyVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "miniStory",
            label: <span className="flex items-center gap-1"><BookOutlined />Mini Story</span>,
            children: <TrackSection label="Mini Story" lrcName="miniStory" audioName="miniStoryAudio" videoName="miniStoryVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "POV",
            label: <span className="flex items-center gap-1"><FileTextOutlined />POV</span>,
            children: <TrackSection label="POV" lrcName="POV" audioName="POVAudio" videoName="POVVideo" handleVttChange={handleVttChange} />,
        },
        {
            key: "comment",
            label: <span className="flex items-center gap-1"><FileTextOutlined />Comment</span>,
            children: <TrackSection label="Comment" lrcName="comment" audioName="commentAudio" videoName="commentVideo" handleVttChange={handleVttChange} />,
        },
    ];

    return (
        <div>
            {/* Header */}
            <div className="mb-6">
                <Breadcrumb items={[
                    { title: <Link to="/admin"><HomeOutlined /> Dashboard</Link> },
                    { title: <Link to="/admin/lesson">Lesson</Link> },
                    { title: id ? "Chỉnh sửa" : "Tạo mới" },
                ]} />
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/admin/lesson")} />
                        <h1 className="text-2xl font-bold text-gray-800 m-0">
                            {id ? "Chỉnh sửa Lesson" : "Tạo Lesson mới"}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {id && (
                            <Button danger icon={<DeleteOutlined />} size="large" onClick={confirmDelete}>
                                Xoá
                            </Button>
                        )}
                        <Button type="primary" size="large" icon={<SaveOutlined />}
                            className="bg-blue-500 min-w-[140px] font-semibold shadow-md"
                            onClick={() => form.submit()}>
                            {id ? "Cập nhật Lesson" : "Tạo Lesson"}
                        </Button>
                    </div>
                </div>
            </div>

            <Form
                layout="vertical"
                colon={false}
                form={form}
                initialValues={initialValues}
                onFinishFailed={(e) => console.log(e)}
                onFinish={onSubmit}
            >
                <Row gutter={24}>
                    {/* Left Column - Main Info */}
                    <Col xs={24} lg={16}>
                        {/* Basic Info */}
                        <Card
                            title={<span className="text-base"><InfoCircleOutlined className="mr-2 text-blue-500" />Thông tin cơ bản</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                label="Tiêu đề"
                                name="title"
                                rules={[{ required: true, message: "Vui lòng nhập tiêu đề!" }]}
                            >
                                <Input size="large" placeholder="Nhập tiêu đề bài học" onChange={handleLessonSlugChange} />
                            </Form.Item>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Slug (URL)"
                                        name="slug"
                                        rules={[{ required: true, message: "Vui lòng nhập slug!" }]}
                                        extra="Tự động tạo từ tiêu đề"
                                    >
                                        <Input size="large" placeholder="slug-cua-lesson" onBlur={handleLessonSlugChange} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Mô tả ngắn"
                                        name="description"
                                    >
                                        <Input.TextArea rows={1} placeholder="Mô tả ngắn cho bài học" showCount maxLength={200} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Content Tracks */}
                        <Card
                            title={<span className="text-base"><SoundOutlined className="mr-2 text-orange-500" />Nội dung bài học</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            bodyStyle={{ paddingTop: 0 }}
                        >
                            <Tabs items={trackTabs} size="large" />
                        </Card>
                    </Col>

                    {/* Right Column - Settings & Image */}
                    <Col xs={24} lg={8}>
                        {/* Settings */}
                        <Card
                            title={<span className="text-base"><SettingOutlined className="mr-2 text-gray-500" />Cài đặt</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: "Vui lòng chọn trạng thái!" }]}
                            >
                                <Select
                                    size="large"
                                    placeholder="Chọn trạng thái"
                                    options={[
                                        { label: "🟢 Hoạt động", value: 1 },
                                        { label: "🔴 Không hoạt động", value: 2 },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Course"
                                name="course_id"
                                rules={[{ required: true, message: "Vui lòng chọn course!" }]}
                            >
                                <Select
                                    showSearch
                                    size="large"
                                    placeholder="Chọn course"
                                    optionFilterProp="children"
                                    options={courses?.map((value) => ({
                                        value: value.id,
                                        label: value.title,
                                    }))}
                                />
                            </Form.Item>
                        </Card>

                        {/* Image */}
                        <Card
                            title={<span className="text-base"><PictureOutlined className="mr-2 text-green-500" />Ảnh bài học</span>}
                            className="mb-5 shadow-sm rounded-xl border-0"
                            headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        >
                            <Form.Item
                                name="lesson_url"
                                rules={[{ required: true, message: "Vui lòng chọn ảnh!" }]}
                            >
                                <CustomUpload type="image" accept=".png, .jpg, .jpeg, .jfif" />
                            </Form.Item>
                            <Divider dashed className="my-2">hoặc nhập URL</Divider>
                            <Form.Item name="lesson_url">
                                <Input size="large" placeholder="https://example.com/image.jpg" />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}
